import {
	Button,
	Checkbox,
	Collapse,
	Typography,
	Radio,
	Row,
	Col,
	Form,
	InputNumber,
	Slider,
} from 'antd';
import { Scrollbars } from 'react-custom-scrollbars-2';
import { useEffect, useState } from 'react';
import { useQueryParameters } from '../../../hooks';
import { useNavigate } from 'react-router';
import {
	ANGLE,
	CHANNEL,
	HEX_BAR,
	PLATE,
	RECTANGLE_BAR,
	RECTANGLE_TUBE,
	ROUND_BAR,
	ROUND_TUBE,
} from '../../../config/constants';
import { getEffectiveShapeName } from '../../../utils/shapeUtils';

const { Title } = Typography;
const { Panel } = Collapse;

interface Props {
	availability_status: string;
	vendors: any[];
	vendors_selected: string;
	shapes: any[];
	shape_selected: string;
	types: any[];
	types_selected: string;
	categories: any[];
	category_selected: string;
	outer_diameter_gte?: number;
	outer_diameter_lte?: number;
	inner_diameter_gte?: number;
	inner_diameter_lte?: number;
	width_gte?: number;
	width_lte?: number;
	height_gte?: number;
	height_lte?: number;
	wall_thickness_gte?: number;
	wall_thickness_lte?: number;
	reset_url?: string;
	show_vendor_filter?: boolean;
}

export const VendorMaterialFilter = (props: Props) => {
	const queryParams = useQueryParameters();
	const navigate = useNavigate();

	const [reset, setReset] = useState(false);
	const [selectedStatus, setSelectedStatus] = useState([
		props.availability_status,
	]);
	const [selectedVendors, setSelectedVendors] = useState<string[]>(
		props.vendors_selected.split(',')
	);
	const [selectedShape, setSelectedShape] = useState<string>(
		props.shape_selected
	);
	const [selectedCategory, setSelectedCategory] = useState<string>(
		props.category_selected
	);
	const [selectedTypes, setSelectedTypes] = useState<string[]>(
		props.types_selected.split(',')
	);

	// Range values
	const [outerDiameterRange, setOuterDiameterRange] = useState<
		[number, number]
	>([0.001, 144]);
	const [innerDiameterRange, setInnerDiameterRange] = useState<
		[number, number]
	>([0.001, 144]);
	const [widthRange, setWidthRange] = useState<[number, number]>([0.001, 144]);
	const [heightRange, setHeightRange] = useState<[number, number]>([
		0.001, 144,
	]);
	const [wallThicknessRange, setWallThicknessRange] = useState<
		[number, number]
	>([0, 999]);

	// Initial range values
	const [initialHeightRange, setInitialHeightRange] = useState<
		[number, number]
	>([0, 144]);
	const [initialWidthRange, setInitialWidthRange] = useState<[number, number]>([
		0, 144,
	]);
	const [initialInnerDiameterRange, setInitialInnerDiameterRange] = useState<
		[number, number]
	>([0, 144]);
	const [initialOuterDiameterRange, setInitialOuterDiameterRange] = useState<
		[number, number]
	>([0, 144]);
	const [initialWallThicknessRange, setInitialWallThicknessRange] = useState<
		[number, number]
	>([0, 999]);

	useEffect(() => {
		if (!props.shape_selected && props.shapes.length > 0) {
			props.shapes.forEach((shape) =>
				shape.display_name === RECTANGLE_BAR ? setSelectedShape(shape.id) : null
			);
		}
	}, [props.shapes]);

	useEffect(() => {
		queryParams.set('availability_status', 'Available');
		queryParams.set(
			'shape',
			props.shapes.find((x) => x.display_name === RECTANGLE_BAR)?.id
		);
		navigate({ search: queryParams.toString() });
	}, []);

	useEffect(() => {
		queryParams.set('availability_status', 'Available');
		queryParams.set(
			'shape',
			props.shapes.find((x) => x.display_name === RECTANGLE_BAR)?.id
		);
		navigate({ search: queryParams.toString() });
	}, [reset]);

	const handleResetFilters = () => {
		setSelectedStatus(['Available']);
		setSelectedVendors([]);
		setSelectedShape(
			props.shapes.find((x) => x.display_name === RECTANGLE_BAR)?.id
		);
		setSelectedTypes([]);

		// reset ranges
		setHeightRange([initialHeightRange[0], initialHeightRange[1]]);
		setWidthRange([initialWidthRange[0], initialWidthRange[1]]);
		setInnerDiameterRange([
			initialInnerDiameterRange[0],
			initialInnerDiameterRange[1],
		]);
		setOuterDiameterRange([
			initialOuterDiameterRange[0],
			initialOuterDiameterRange[1],
		]);
		setWallThicknessRange([
			initialWallThicknessRange[0],
			initialWallThicknessRange[1],
		]);

		// reset initials
		setInitialHeightRange([initialHeightRange[0], initialHeightRange[1]]);
		setInitialWidthRange([initialWidthRange[0], initialWidthRange[1]]);
		setInitialInnerDiameterRange([
			initialInnerDiameterRange[0],
			initialInnerDiameterRange[1],
		]);
		setInitialOuterDiameterRange([
			initialOuterDiameterRange[0],
			initialOuterDiameterRange[1],
		]);
		setInitialWallThicknessRange([
			initialWallThicknessRange[0],
			initialWallThicknessRange[1],
		]);

		queryParams.delete('availability_status');
		queryParams.delete('vendors');
		queryParams.delete('shape');
		queryParams.delete('types');
		queryParams.delete('outer_diameter__gte');
		queryParams.delete('outer_diameter__lte');
		queryParams.delete('inner_diameter__gte');
		queryParams.delete('inner_diameter__lte');
		queryParams.delete('width__gte');
		queryParams.delete('width__lte');
		queryParams.delete('height__gte');
		queryParams.delete('height__lte');
		queryParams.delete('wall_thickness__gte');
		queryParams.delete('wall_thickness__lte');

		if (props.reset_url) {
			navigate(props.reset_url);
		} else {
			navigate('/material/vendor-materials');
		}
		setReset(!reset);
	};

	const handleSetQueryParams = (key: string, value: string) => {
		queryParams.set(key, value);
		queryParams.set('page', '1');
		navigate({ search: queryParams.toString() });
	};

	const handleDeleteQueryParams = (key: string) => {
		queryParams.delete(key);
		navigate({ search: queryParams.toString() });
	};

	const handleStatusChange = (checkedValues: string[]) => {
		if (
			checkedValues.includes('Available') &&
			checkedValues.includes('Unavailable')
		) {
			const lastSelected = checkedValues[checkedValues.length - 1];
			setSelectedStatus([lastSelected]);
			handleSetQueryParams('availability_status', lastSelected);
		} else {
			setSelectedStatus(checkedValues);
			if (checkedValues.length === 0) {
				queryParams.delete('availability_status');
			}
			navigate({ search: queryParams.toString() });
		}
	};

	const handleVendorChange = (checkedValues: string[]) => {
		setSelectedVendors(checkedValues);
		handleSetQueryParams('vendors', checkedValues.join(','));
	};

	const handleShapeChange = (e: any): void => {
		const value = e.target.value;
		setSelectedShape(value);
		const shapeName = getShapeNameById(value);

		if (
			shapeName === RECTANGLE_BAR ||
			shapeName === PLATE ||
			shapeName === CHANNEL ||
			shapeName === ANGLE
		) {
			queryParams.delete('outer_diameter__gte');
			queryParams.delete('outer_diameter__lte');
			queryParams.delete('inner_diameter__gte');
			queryParams.delete('inner_diameter__lte');
			queryParams.delete('wall_thickness__gte');
			queryParams.delete('wall_thickness__lte');
		} else if (shapeName === 'Round') {
			queryParams.delete('width__gte');
			queryParams.delete('width__lte');
			queryParams.delete('height__gte');
			queryParams.delete('height__lte');
			queryParams.delete('inner_diameter__gte');
			queryParams.delete('inner_diameter__lte');
			queryParams.delete('wall_thickness__gte');
			queryParams.delete('wall_thickness__lte');
		} else if (shapeName === 'Tube') {
			queryParams.delete('width__gte');
			queryParams.delete('width__lte');
			queryParams.delete('height__gte');
			queryParams.delete('height__lte');
			queryParams.delete('outer_diameter__gte');
			queryParams.delete('outer_diameter__lte');
			queryParams.delete('wall_thickness__gte');
			queryParams.delete('wall_thickness__lte');
		}

		handleSetQueryParams('shape', value);
	};

	const handleCategoryChange = (e: any) => {
		setSelectedCategory(e.target.value);
		handleSetQueryParams('category', e.target.value);
	};

	const handleTypeChange = (checkedValues: string[]) => {
		setSelectedTypes(checkedValues);
		handleSetQueryParams('types', checkedValues.join(','));
	};

	const handleOuterDiameterChange = (value: [number, number]) => {
		setOuterDiameterRange(value);
	};

	const handleOuterDiameterChangeComplete = (value: [number, number]) => {
		setOuterDiameterRange(value);
		handleSetQueryParams('outer_diameter__gte', String(value[0]));
		handleSetQueryParams('outer_diameter__lte', String(value[1]));
	};

	const handleHeightChange = (value: [number, number]) => {
		setHeightRange(value);
	};

	const handleHeightChangeComplete = (value: [number, number]) => {
		setHeightRange(value);
		handleSetQueryParams('height__gte', String(value[0]));
		handleSetQueryParams('height__lte', String(value[1]));
	};

	const handleWidthChange = (value: [number, number]) => {
		setWidthRange(value);
	};

	const handleWidthChangeComplete = (value: [number, number]) => {
		setWidthRange(value);
		handleSetQueryParams('width__gte', String(value[0]));
		handleSetQueryParams('width__lte', String(value[1]));
	};

	const handleInnerDiameterChange = (value: [number, number]) => {
		setInnerDiameterRange(value);
	};

	const handleInnerDiameterChangeComplete = (value: [number, number]) => {
		setInnerDiameterRange(value);
		handleSetQueryParams('inner_diameter__gte', String(value[0]));
		handleSetQueryParams('inner_diameter__lte', String(value[1]));
	};

	const handleWallThicknessChange = (value: [number, number]) => {
		setWallThicknessRange(value);
	};

	const handleWallThicknessChangeComplete = (value: [number, number]) => {
		setWallThicknessRange(value);
		handleSetQueryParams('wall_thickness__gte', String(value[0]));
		handleSetQueryParams('wall_thickness__lte', String(value[1]));
	};

	const getShapeNameById = (id: string | undefined | null) => {
		if (id) {
			return getEffectiveShapeName(props.shapes.find((x) => x.id === id)?.display_name);
		}
	};

	return (
		<div
			style={{
				background: '#FBFBFB',
				border: '1px solid #D9D9D9',
				borderRadius: '2px',
				flex: '0 0 250px',
			}}
		>
			<div
				style={{
					background: '#FBFBFB',
					borderBottom: '1px solid #D9D9D9',
					borderRadius: '2px',
				}}
			>
				<div className="flex justify-between items-center p-3">
					<Title level={5} style={{ margin: 0 }}>
						Filters
					</Title>
					<div>
						<Button type="link" onClick={handleResetFilters}>
							Reset
						</Button>
					</div>
				</div>
			</div>
			<div>
				<Collapse
					defaultActiveKey={['0', '2']}
					style={{ border: 'none' }}
					ghost={true}
				>
					<Panel
						header="Availability Status"
						key="0"
						style={{
							width: '100%',
							backgroundColor: 'white',
							borderBottom: '1px solid #d9d9d9',
						}}
					>
						<Checkbox.Group
							onChange={handleStatusChange}
							value={selectedStatus}
						>
							<div style={{ marginBottom: '8px', width: '100%' }}>
								<Checkbox value="Available">Available</Checkbox>
							</div>
							<div style={{ marginBottom: '8px', width: '100%' }}>
								<Checkbox value="Unavailable">Unavailable</Checkbox>
							</div>
						</Checkbox.Group>
					</Panel>

					{props.show_vendor_filter && (
						<Panel
							header="Vendor"
							key="1"
							style={{
								width: '100%',
								backgroundColor: 'white',
								borderBottom: '1px solid #d9d9d9',
							}}
						>
							<Checkbox.Group
								value={selectedVendors}
								onChange={handleVendorChange}
							>
								<div className="flex flex-col">
									{' '}
									{/* Asegura que los elementos estén en una columna */}
									{props.vendors.map((x) => (
										<div key={x.id} className="mb-2">
											<Checkbox value={x.id}>{x.display_name}</Checkbox>
										</div>
									))}
								</div>
							</Checkbox.Group>
						</Panel>
					)}

					<Panel
						header="Shape"
						key="2"
						style={{
							width: '100%',
							backgroundColor: 'white',
							borderBottom: '1px solid #d9d9d9',
						}}
					>
						<Radio.Group value={selectedShape} onChange={handleShapeChange}>
							{props.shapes.map((x) => (
								<div key={x.id} style={{ marginBottom: '8px' }}>
									<Radio value={x.id}>{x.display_name}</Radio>
								</div>
							))}
						</Radio.Group>
					</Panel>

					<Panel
						header={
							<div className="flex flex-row justify-between w-full">
								Category
								<a
									onClick={(e) => {
										e.stopPropagation();
										setSelectedCategory('');
										handleDeleteQueryParams('category');
									}}
								>
									Reset
								</a>
							</div>
						}
						key="3"
						style={{
							width: '100%',
							backgroundColor: 'white',
							borderBottom: '1px solid #d9d9d9',
						}}
					>
						<Radio.Group
							value={selectedCategory}
							onChange={handleCategoryChange}
						>
							{props.categories.map((x) => (
								<div key={x.id} style={{ marginBottom: '8px' }}>
									<Radio value={x.id}>{x.display_name}</Radio>
								</div>
							))}
						</Radio.Group>
					</Panel>

					<Panel
						header="Type"
						key="4"
						style={{
							width: '100%',
							backgroundColor: 'white',
							borderBottom: '1px solid #d9d9d9',
						}}
					>
						<Scrollbars style={{ height: 250 }}>
							<Checkbox.Group value={selectedTypes} onChange={handleTypeChange}>
								{props.types.map((x) => (
									<div
										key={x.id}
										style={{ marginBottom: '8px', width: '100%' }}
									>
										<Checkbox value={x.id}>{x.display_name}</Checkbox>
									</div>
								))}
							</Checkbox.Group>
						</Scrollbars>
					</Panel>

					{(getShapeNameById(selectedShape) === RECTANGLE_BAR ||
						getShapeNameById(selectedShape) === RECTANGLE_TUBE ||
						getShapeNameById(selectedShape) === PLATE ||
						getShapeNameById(selectedShape) === ANGLE ||
						getShapeNameById(selectedShape) === CHANNEL ||
						getShapeNameById(selectedShape) === HEX_BAR) && (
						<>
							<Panel
								header="Height"
								key="height"
								style={{
									width: '100%',
									backgroundColor: 'white',
									borderBottom: '1px solid #d9d9d9',
								}}
							>
								<Row gutter={8} align="middle">
									<Col span={12}>
										<Form.Item label="Min">
											<InputNumber
												min={initialHeightRange[0]}
												max={initialHeightRange[1]}
												step={0.25}
												value={heightRange[0]}
												onChange={(value) => {
													if (value) {
														setHeightRange([Number(value), heightRange[1]]);
														handleHeightChange([Number(value), heightRange[1]]);
													}
												}}
												onKeyDown={(e) => {
													if (e.key === 'Enter') {
														handleHeightChangeComplete([
															heightRange[0],
															heightRange[1],
														]);
													}
												}}
												style={{ width: '100%' }}
												suffix="in"
											/>
										</Form.Item>
									</Col>
									<Col span={12}>
										<Form.Item label="Max">
											<InputNumber
												min={initialHeightRange[0]}
												max={initialHeightRange[1]}
												step={0.25}
												value={heightRange[1]}
												onChange={(value) => {
													if (value) {
														setHeightRange([heightRange[0], Number(value)]);
														handleHeightChange([heightRange[0], Number(value)]);
													}
												}}
												onKeyDown={(e) => {
													if (e.key === 'Enter') {
														handleHeightChangeComplete([
															heightRange[0],
															heightRange[1],
														]);
													}
												}}
												style={{ width: '100%' }}
												suffix="in"
											/>
										</Form.Item>
									</Col>
									<Col span={24}>
										<Slider
											range
											min={initialHeightRange[0]}
											max={initialHeightRange[1]}
											step={0.25}
											value={heightRange}
											onChange={(value) => {
												handleHeightChange([value[0], value[1]]);
											}}
											onChangeComplete={(value) => {
												handleHeightChangeComplete([value[0], value[1]]);
											}}
											tooltip={{ formatter: (value) => `${value}"` }}
										/>
									</Col>
								</Row>
							</Panel>
							<Panel
								header="Width"
								key="width"
								style={{
									width: '100%',
									backgroundColor: 'white',
									borderBottom: '1px solid #d9d9d9',
								}}
							>
								<Row gutter={8} align="middle">
									<Col span={12}>
										<Form.Item label="Min">
											<InputNumber
												min={initialWidthRange[0]}
												max={initialWidthRange[1]}
												step={0.25}
												value={widthRange[0]}
												onChange={(value) => {
													if (value) {
														setWidthRange([Number(value), widthRange[1]]);
														handleWidthChange([Number(value), widthRange[1]]);
													}
												}}
												onKeyDown={(e) => {
													if (e.key === 'Enter') {
														handleWidthChangeComplete([
															widthRange[0],
															widthRange[1],
														]);
													}
												}}
												style={{ width: '100%' }}
												suffix="in"
											/>
										</Form.Item>
									</Col>
									<Col span={12}>
										<Form.Item label="Max">
											<InputNumber
												min={initialWidthRange[0]}
												max={initialWidthRange[1]}
												step={0.25}
												value={widthRange[1]}
												onChange={(value) => {
													if (value) {
														setWidthRange([widthRange[0], Number(value)]);
														handleWidthChange([widthRange[0], Number(value)]);
													}
												}}
												onKeyDown={(e) => {
													if (e.key === 'Enter') {
														handleWidthChangeComplete([
															widthRange[0],
															widthRange[1],
														]);
													}
												}}
												style={{ width: '100%' }}
												suffix="in"
											/>
										</Form.Item>
									</Col>
									<Col span={24}>
										<Slider
											range
											min={initialWidthRange[0]}
											max={initialWidthRange[1]}
											step={0.25}
											value={widthRange}
											onChange={(value) => {
												handleWidthChange([value[0], value[1]]);
											}}
											onChangeComplete={(value) => {
												handleWidthChangeComplete([value[0], value[1]]);
											}}
											tooltip={{ formatter: (value) => `${value}"` }}
										/>
									</Col>
								</Row>
							</Panel>
						</>
					)}

					{getShapeNameById(selectedShape) === ROUND_BAR ||
					getShapeNameById(selectedShape) === ROUND_TUBE ? (
						<Panel
							header="Outer Diameter"
							key="outer_diameter"
							style={{
								width: '100%',
								backgroundColor: 'white',
								borderBottom: '1px solid #d9d9d9',
							}}
						>
							<Row gutter={8} align="middle">
								<Col span={12}>
									<Form.Item label="Min">
										<InputNumber
											min={initialOuterDiameterRange[0]}
											max={initialOuterDiameterRange[1]}
											step={0.25}
											value={outerDiameterRange[0]}
											onChange={(value) => {
												setOuterDiameterRange([
													Number(value),
													outerDiameterRange[1],
												]);
												handleOuterDiameterChange([
													Number(value),
													outerDiameterRange[1],
												]);
											}}
											onKeyDown={(e) => {
												if (e.key === 'Enter') {
													handleOuterDiameterChangeComplete([
														outerDiameterRange[0],
														outerDiameterRange[1],
													]);
												}
											}}
											style={{ width: '100%' }}
											suffix="in"
										/>
									</Form.Item>
								</Col>
								<Col span={12}>
									<Form.Item label="Max">
										<InputNumber
											min={initialOuterDiameterRange[0]}
											max={initialOuterDiameterRange[1]}
											step={0.25}
											value={outerDiameterRange[1]}
											onChange={(value) => {
												if (value) {
													setOuterDiameterRange([
														outerDiameterRange[0],
														Number(value),
													]);
													handleOuterDiameterChange([
														outerDiameterRange[0],
														Number(value),
													]);
												}
											}}
											onKeyDown={(e) => {
												if (e.key === 'Enter') {
													handleOuterDiameterChangeComplete([
														outerDiameterRange[0],
														outerDiameterRange[1],
													]);
												}
											}}
											style={{ width: '100%' }}
											suffix="in"
										/>
									</Form.Item>
								</Col>
								<Col span={24}>
									<Slider
										range
										min={initialOuterDiameterRange[0]}
										max={initialOuterDiameterRange[1]}
										step={0.25}
										value={outerDiameterRange}
										onChange={(value) => {
											handleOuterDiameterChange([value[0], value[1]]);
										}}
										onChangeComplete={(value) => {
											handleOuterDiameterChangeComplete([value[0], value[1]]);
										}}
										tooltip={{ formatter: (value) => `${value}"` }}
									/>
								</Col>
							</Row>
						</Panel>
					) : null}

					{getShapeNameById(selectedShape) === ROUND_TUBE && (
						<Panel
							header="Inner Diameter"
							key="inner_diameter"
							style={{
								width: '100%',
								backgroundColor: 'white',
								borderBottom: '1px solid #d9d9d9',
							}}
						>
							<Row gutter={8} align="middle">
								<Col span={12}>
									<Form.Item label="Min">
										<InputNumber
											min={initialInnerDiameterRange[0]}
											max={initialInnerDiameterRange[1]}
											step={0.25}
											value={innerDiameterRange[0]}
											onChange={(value) => {
												if (value) {
													setInnerDiameterRange([
														Number(value),
														innerDiameterRange[1],
													]);
													handleInnerDiameterChange([
														Number(value),
														innerDiameterRange[1],
													]);
												}
											}}
											onKeyDown={(e) => {
												if (e.key === 'Enter') {
													handleInnerDiameterChangeComplete([
														innerDiameterRange[0],
														innerDiameterRange[1],
													]);
												}
											}}
											style={{ width: '100%' }}
											suffix="in"
										/>
									</Form.Item>
								</Col>
								<Col span={12}>
									<Form.Item label="Max">
										<InputNumber
											min={initialInnerDiameterRange[0]}
											max={initialInnerDiameterRange[1]}
											step={0.25}
											value={innerDiameterRange[1]}
											onChange={(value) => {
												if (value) {
													setInnerDiameterRange([
														innerDiameterRange[0],
														Number(value),
													]);
													handleInnerDiameterChange([
														innerDiameterRange[0],
														Number(value),
													]);
												}
											}}
											onKeyDown={(e) => {
												if (e.key === 'Enter') {
													handleInnerDiameterChangeComplete([
														innerDiameterRange[0],
														innerDiameterRange[1],
													]);
												}
											}}
											style={{ width: '100%' }}
											suffix="in"
										/>
									</Form.Item>
								</Col>
								<Col span={24}>
									<Slider
										range
										min={initialInnerDiameterRange[0]}
										max={initialInnerDiameterRange[1]}
										step={0.25}
										value={innerDiameterRange}
										onChange={(value) => {
											handleInnerDiameterChange([value[0], value[1]]);
										}}
										onChangeComplete={(value) => {
											handleInnerDiameterChangeComplete([value[0], value[1]]);
										}}
										tooltip={{ formatter: (value) => `${value}"` }}
									/>
								</Col>
							</Row>
						</Panel>
					)}

					{(getShapeNameById(selectedShape) === ANGLE ||
						getShapeNameById(selectedShape) === CHANNEL ||
						getShapeNameById(selectedShape) === RECTANGLE_TUBE) && (
						<>
							<Panel
								header="Wall Thickness"
								key="wall_thickness"
								style={{
									width: '100%',
									backgroundColor: 'white',
									borderBottom: '1px solid #d9d9d9',
								}}
							>
								<Row gutter={8} align="middle">
									<Col span={12}>
										<Form.Item label="Min">
											<InputNumber
												min={initialWallThicknessRange[0]}
												max={initialWallThicknessRange[1]}
												step={0.25}
												value={wallThicknessRange[0]}
												onChange={(value) => {
													if (value) {
														setWallThicknessRange([
															Number(value),
															wallThicknessRange[1],
														]);
														handleWallThicknessChange([
															Number(value),
															wallThicknessRange[1],
														]);
													}
												}}
												onKeyDown={(e) => {
													if (e.key === 'Enter') {
														handleWallThicknessChangeComplete([
															wallThicknessRange[0],
															wallThicknessRange[1],
														]);
													}
												}}
												style={{ width: '100%' }}
												suffix="in"
											/>
										</Form.Item>
									</Col>
									<Col span={12}>
										<Form.Item label="Max">
											<InputNumber
												min={initialWallThicknessRange[0]}
												max={initialWallThicknessRange[1]}
												step={0.25}
												value={wallThicknessRange[1]}
												onChange={(value) => {
													if (value) {
														setWallThicknessRange([
															wallThicknessRange[0],
															Number(value),
														]);
														handleWallThicknessChange([
															wallThicknessRange[0],
															Number(value),
														]);
													}
												}}
												onKeyDown={(e) => {
													if (e.key === 'Enter') {
														handleWallThicknessChangeComplete([
															wallThicknessRange[0],
															wallThicknessRange[1],
														]);
													}
												}}
												style={{ width: '100%' }}
												suffix="in"
											/>
										</Form.Item>
									</Col>
									<Col span={24}>
										<Slider
											range
											min={initialWallThicknessRange[0]}
											max={initialWallThicknessRange[1]}
											step={0.25}
											value={wallThicknessRange}
											onChange={(value) => {
												handleWallThicknessChange([value[0], value[1]]);
											}}
											onChangeComplete={(value) => {
												handleWallThicknessChangeComplete([value[0], value[1]]);
											}}
											tooltip={{ formatter: (value) => `${value}"` }}
										/>
									</Col>
								</Row>
							</Panel>
						</>
					)}
				</Collapse>
			</div>
		</div>
	);
};
